import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useGeneratedTextPaginatedList } from "../../../hooks/useGeneratedTextPaginatedList";
import { GenerateTextCard } from "../../../components/GenerateTextCard";

import { VocabularyPreviewCard } from "../../../components/VocabularyPreviewCard";
import { ArrowRightIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useStreamingTextIntegrator } from "../../../hooks/useStreamingTextIntegrator";
import { useUserSettings } from "../../../hooks/useUserSettings";
import { useLoginStatus } from "../../../hooks/useLoginStatus";
import { useUserSettingsUpdate } from "../../../hooks/useUserSettingsUpdate";
import { ReepriseTextCard } from "../../../components/ReepriseTextCard";
import {
  useNotifications,
  useMarkNotificationAsRead,
} from "../../../hooks/useNotifications";
import useTranslation from "../../../hooks/useTranslation";
import type { GeneratedText } from "../../../hooks/types/GeneratedTexts";
import { useLanguageSpace } from "../../../hooks/useLanguageSpace";
import { useDailyTextCreate } from "../../../hooks/useDailyTextCreate";
import { REEPRISE_LIST_QUERY_KEY } from "../../../hooks/useGeneratedTextPaginatedList";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import { getCsrfCookie } from "../../../lib/utils";

import {
  getLocalIsoDate,
  getLocalIsoDateFromTimestamp,
} from "../../../utils/getLocalIsoDate";
import { resolvePendingGeneratedTextTitle } from "../../../utils/resolvePendingGeneratedTextTitle";
import { partitionGeneratedTextsByReadState, sortGeneratedTexts } from "../../../utils/reepriseFeedOrdering";
import LoadingScreen from "../../../components/LoadingScreen";
import { ItalianPracticeCallout } from "../../../components/ItalianPracticeCallout";

const MOBILE_BOTTOM_NAV_MEDIA_QUERY = "(max-width: 767px)";
const EMPTY_FEED_GUIDANCE_DISMISSED_KEY =
  "reeprise-empty-feed-guidance-dismissed-v1";

const MOBILE_REEPRISE_PAGE_WASH_BY_LANGUAGE: Partial<Record<string, string>> = {
  ENGLISH:
    "linear-gradient(180deg, rgba(244,240,228,0.82) 0px, rgba(210,219,206,0.34) 182px, rgba(248,250,252,0.96) 430px), radial-gradient(circle at 78% 0%, rgba(255,250,234,0.24) 0%, rgba(255,250,234,0) 30%)",
  GERMAN:
    "linear-gradient(180deg, rgba(244,240,228,0.82) 0px, rgba(238,215,177,0.34) 182px, rgba(248,250,252,0.96) 430px), radial-gradient(circle at 78% 0%, rgba(255,250,234,0.24) 0%, rgba(255,250,234,0) 30%)",
  FRENCH:
    "linear-gradient(180deg, rgba(244,240,228,0.82) 0px, rgba(236,211,178,0.34) 182px, rgba(248,250,252,0.96) 430px), radial-gradient(circle at 78% 0%, rgba(255,250,234,0.24) 0%, rgba(255,250,234,0) 30%)",
  ITALIAN:
    "linear-gradient(180deg, rgba(244,240,228,0.82) 0px, rgba(208,196,228,0.34) 182px, rgba(248,250,252,0.96) 430px), radial-gradient(circle at 78% 0%, rgba(255,250,234,0.24) 0%, rgba(255,250,234,0) 30%)",
};

function useMobileBottomNavLayout() {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return window.matchMedia(MOBILE_BOTTOM_NAV_MEDIA_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const mediaQuery = window.matchMedia(MOBILE_BOTTOM_NAV_MEDIA_QUERY);
    const handleChange = (event: MediaQueryListEvent) => {
      setIsMobile(event.matches);
    };

    setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleChange);
    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  return isMobile;
}

const DAILY_TEXT_DONE_MARKER_KEY = "dailyTextDoneByLanguage";
type DailyDoneMarker = string | {
  date: string;
  textId?: number;
  title?: string;
  motherTongueTitle?: string;
};

function getDoneMarkerDate(marker: DailyDoneMarker | undefined) {
  if (!marker) {
    return undefined;
  }
  return typeof marker === "string" ? marker : marker.date;
}

function getDoneMarkerTextId(marker: DailyDoneMarker | undefined) {
  if (!marker || typeof marker === "string") {
    return undefined;
  }
  return marker.textId;
}

function upsertDoneMarker(
  markers: Record<string, DailyDoneMarker>,
  key: string,
  nextMarker: {
    date: string;
    textId?: number;
    title?: string;
    motherTongueTitle?: string;
  },
) {
  const currentMarker = markers[key];
  if (
    typeof currentMarker !== "string" &&
    currentMarker?.date === nextMarker.date &&
    currentMarker?.textId === nextMarker.textId &&
    currentMarker?.title === nextMarker.title &&
    currentMarker?.motherTongueTitle === nextMarker.motherTongueTitle
  ) {
    return markers;
  }

  return {
    ...markers,
    [key]: nextMarker,
  };
}

function getDoneMarkerTitle(marker: DailyDoneMarker | undefined) {
  if (!marker || typeof marker === "string") {
    return undefined;
  }
  return marker.title;
}

function getDoneMarkerMotherTongueTitle(marker: DailyDoneMarker | undefined) {
  if (!marker || typeof marker === "string") {
    return undefined;
  }
  return marker.motherTongueTitle;
}

function parseAiTitleForDailyCard(raw: string): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();

  if (trimmed.startsWith("{")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (
        parsed &&
        typeof parsed === "object" &&
        typeof parsed.title === "string"
      ) {
        const normalized = parsed.title.trim();
        return normalized || null;
      }
    } catch {
      // fall through
    }
  }

  const unquoted =
    trimmed.startsWith('"') && trimmed.endsWith('"')
      ? trimmed.slice(1, -1).replace(/\\n/g, "\n").replace(/\\"/g, '"')
      : trimmed;

  const titleLineMatch = unquoted.match(/^(?:title|titel)\s*:\s*(.+)$/im);
  if (titleLineMatch?.[1]) {
    const value = titleLineMatch[1].trim();
    return value || null;
  }

  return null;
}

function resolvePendingDailyTitle(text: GeneratedText | null | undefined): string | null {
  if (!text || text.status !== "PENDING") {
    return null;
  }
  return resolvePendingGeneratedTextTitle({
    status: text.status,
    topic: text.topic,
    cachedPromptTopic: "",
  });
}

function resolveDailyCardTitle(text: GeneratedText | null | undefined): string | null {
  if (!text) {
    return null;
  }
  if (text.status === "COMPLETE") {
    return parseAiTitleForDailyCard(text.chatgpt_output || "") || text.topic || null;
  }
  return resolvePendingDailyTitle(text);
}

function scoreDailyTextForCardDisplay(
  text: GeneratedText | null | undefined,
): number {
  if (!text) {
    return -1;
  }
  const resolvedTitle = resolveDailyCardTitle(text);
  if (text.status === "COMPLETE") {
    return resolvedTitle ? 4 : 3;
  }
  return resolvedTitle ? 2 : 1;
}

function preferDisplayReadyDailyText(
  primary: GeneratedText | null | undefined,
  fallback: GeneratedText | null | undefined,
): GeneratedText | undefined {
  if (!primary) {
    return fallback ?? undefined;
  }
  if (!fallback) {
    return primary;
  }

  const sameLogicalDaily =
    primary.id === fallback.id ||
    (primary.origin === "DAILY_NUDGE" &&
      fallback.origin === "DAILY_NUDGE" &&
      primary.language === fallback.language &&
      getLocalIsoDateFromTimestamp(primary.created_at) ===
        getLocalIsoDateFromTimestamp(fallback.created_at));
  if (!sameLogicalDaily) {
    return primary;
  }

  return scoreDailyTextForCardDisplay(fallback) >
    scoreDailyTextForCardDisplay(primary)
    ? fallback
    : primary;
}

function compactDailyDoneTitle(
  title: string | null,
  maxChars = 68,
): string | null {
  if (!title) {
    return null;
  }
  const cleaned = title
    .replace(/\s+/g, " ")
    .replace(/^["'`([{<\s]+/, "")
    .trim();
  if (!cleaned) {
    return null;
  }
  if (cleaned.length <= maxChars) {
    return cleaned;
  }
  const clipped = cleaned.slice(0, maxChars);
  const boundary = clipped.lastIndexOf(" ");
  const safeClip = (
    boundary > 28 ? clipped.slice(0, boundary) : clipped
  ).trimEnd();
  return `${safeClip}...`;
}

function isDailyNudgeText(text: GeneratedText) {
  const topicLower = (text.topic || "").toLowerCase();
  return (
    text.origin === "DAILY_NUDGE" ||
    topicLower.startsWith("daily learning text")
  );
}

function findMostRecentDailyNudgeTextForDate(
  texts: GeneratedText[],
  isoDate: string,
) {
  return sortGeneratedTexts(texts).find((text) => {
    const createdDate = getLocalIsoDateFromTimestamp(text.created_at);
    return createdDate === isoDate && isDailyNudgeText(text);
  });
}

function getDailyCardThemeClasses(languageSpace: string) {
  const base = {
    container: "relative mb-4 overflow-hidden rounded-2xl p-4 sm:p-5",
    badge: "text-[0.72rem] font-medium tracking-[0.01em]",
    date: "font-serif text-xs font-semibold",
    heading:
      "break-words font-serif text-[22px] leading-tight tracking-[0.1px] hyphens-auto sm:text-[24px] lg:text-[30px]",
    body: "mt-2 text-sm leading-relaxed",
    action:
      "inline-flex items-center gap-1.5 self-start text-sm font-semibold tracking-[0.01em] transition disabled:cursor-not-allowed disabled:opacity-50",
  };

  if (languageSpace === "ENGLISH") {
    return {
      ...base,
      container: `${base.container} bg-[#e5e9d8]`,
      badgeText: `${base.date} text-[#6b705c]`,
      dateText: `${base.date} text-[#6b705c]`,
      headingText: `${base.heading} text-[#3a3d34]`,
      bodyText: `${base.body} text-[#5c604e]`,
      actionText: `${base.action} text-[#6b705c] hover:text-[#3a3d34]`,
    };
  }
  if (languageSpace === "FRENCH") {
    return {
      ...base,
      container: `${base.container} bg-[#ebe5d4]`,
      badgeText: `${base.date} text-[#8a7a5c]`,
      dateText: `${base.date} text-[#8a7a5c]`,
      headingText: `${base.heading} text-[#4a3f2f]`,
      bodyText: `${base.body} text-[#6b5d45]`,
      actionText: `${base.action} text-[#8a7a5c] hover:text-[#4a3f2f]`,
    };
  }
  if (languageSpace === "ITALIAN") {
    return {
      ...base,
      container: `${base.container} bg-[#e3e1f1]`,
      badgeText: `${base.date} text-[#6e6a8a]`,
      dateText: `${base.date} text-[#6e6a8a]`,
      headingText: `${base.heading} text-[#35324a]`,
      bodyText: `${base.body} text-[#565274]`,
      actionText: `${base.action} text-[#6e6a8a] hover:text-[#35324a]`,
    };
  }
  if (languageSpace === "GERMAN") {
    return {
      ...base,
      container: `${base.container} bg-[#ebe0c8]`,
      badgeText: `${base.date} text-[#8a7a52]`,
      dateText: `${base.date} text-[#8a7a52]`,
      headingText: `${base.heading} text-[#4a3a22]`,
      bodyText: `${base.body} text-[#6b5d3e]`,
      actionText: `${base.action} text-[#8a7a52] hover:text-[#4a3a22]`,
    };
  }
  return {
    ...base,
    container: `${base.container} bg-[#e8ecf1]`,
    badgeText: `${base.date} text-slate-500`,
    dateText: `${base.date} text-slate-500`,
    headingText: `${base.heading} text-slate-800`,
    bodyText: `${base.body} text-slate-600`,
    actionText: `${base.action} text-slate-500 hover:text-slate-700`,
  };
}

export const Route = createFileRoute("/app/reeprise/")({
  validateSearch: (search: Record<string, unknown>) => ({
    mobileView: search.mobileView === "create" ? "create" : undefined,
  }),
  component: Reeprise,
});

function Reeprise() {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const { mobileView } = Route.useSearch();
  const { languageSpace } = useLanguageSpace("ENGLISH");
  const isMobileBottomNavLayout = useMobileBottomNavLayout();
  const paginatedGeneratedTextList = useGeneratedTextPaginatedList();
  const [showComposeGuidePulse, setShowComposeGuidePulse] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [guidanceDismissedByLanguage, setGuidanceDismissedByLanguage] =
    useLocalStorage<Record<string, boolean>>(
      EMPTY_FEED_GUIDANCE_DISMISSED_KEY,
      {},
    );
  const todayIso = getLocalIsoDate();

  const userSettingsQuery = useUserSettings();

  //HACK this establishes the WS connection
  useStreamingTextIntegrator({ textId: 0 });

  const visibleTexts = (paginatedGeneratedTextList.data?.pages ?? [])
    .flatMap((page) => page.results)
    .filter((text) =>
      languageSpace === "ALL" ? true : text.language === languageSpace,
    );
  const sortedVisibleTexts = sortGeneratedTexts(visibleTexts);
  const isDailyCalloutVisible = new Date().getHours() >= 6;
  const featuredTodayDailyText = isDailyCalloutVisible
    ? findMostRecentDailyNudgeTextForDate(sortedVisibleTexts, todayIso)
    : undefined;
  const feedTexts = featuredTodayDailyText
    ? sortedVisibleTexts.filter((text) => text.id !== featuredTodayDailyText.id)
    : sortedVisibleTexts;
  const { unreadTexts, readTexts } = partitionGeneratedTextsByReadState(feedTexts);
  const hasVisibleFeedItems = unreadTexts.length + readTexts.length > 0;
  const hasDismissedGuide = guidanceDismissedByLanguage[languageSpace] === true;
  const shouldShowEmptyFeedGuide = !hasVisibleFeedItems && !hasDismissedGuide;
  const mobileReeprisePageWash =
    MOBILE_REEPRISE_PAGE_WASH_BY_LANGUAGE[languageSpace];
  const pendingMobileComposeFocusRef = useRef(false);
  const isMobileCreateView =
    isMobileBottomNavLayout && mobileView === "create";

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const applyPreference = () => setPrefersReducedMotion(mediaQuery.matches);
    applyPreference();
    mediaQuery.addEventListener("change", applyPreference);
    return () => {
      mediaQuery.removeEventListener("change", applyPreference);
    };
  }, []);

  useEffect(() => {
    if (!shouldShowEmptyFeedGuide || prefersReducedMotion) {
      setShowComposeGuidePulse(false);
      return;
    }
    setShowComposeGuidePulse(true);
    const timeout = window.setTimeout(() => {
      setShowComposeGuidePulse(false);
    }, 5200);
    return () => window.clearTimeout(timeout);
  }, [prefersReducedMotion, shouldShowEmptyFeedGuide]);

  const markGuideDismissed = useCallback(() => {
    if (hasDismissedGuide) {
      return;
    }
    setGuidanceDismissedByLanguage((prev) => ({
      ...prev,
      [languageSpace]: true,
    }));
  }, [hasDismissedGuide, languageSpace, setGuidanceDismissedByLanguage]);

  const focusExistingTopicInput = useCallback(
    (smooth: boolean) => {
      if (typeof window === "undefined") {
        return;
      }
      const topicInput = document.querySelector(
        'textarea[name="topic"]',
      ) as HTMLTextAreaElement | null;
      if (!topicInput) {
        return;
      }

      const rect = topicInput.getBoundingClientRect();
      const isFullyInViewport =
        rect.top >= 0 && rect.bottom <= window.innerHeight;
      if (!isFullyInViewport) {
        topicInput.scrollIntoView({
          behavior: smooth && !prefersReducedMotion ? "smooth" : "auto",
          block: "center",
        });
      }
      window.setTimeout(
        () => {
          topicInput.focus();
          setShowComposeGuidePulse(true);
          window.setTimeout(() => setShowComposeGuidePulse(false), 1800);
        },
        isFullyInViewport ? 0 : 220,
      );
    },
    [prefersReducedMotion],
  );

  const openMobileCreateView = useCallback(
    (focusTopic: boolean) => {
      pendingMobileComposeFocusRef.current = focusTopic;
      void navigate({
        to: "/app/reeprise",
        search: { mobileView: "create" },
        replace: true,
      });
    },
    [navigate, pendingMobileComposeFocusRef],
  );

  const closeMobileCreateView = useCallback(() => {
    pendingMobileComposeFocusRef.current = false;
    void navigate({
      to: "/app/reeprise",
      search: { mobileView: undefined },
      replace: true,
    });
  }, [navigate, pendingMobileComposeFocusRef]);

  const focusTopicInputInCompose = useCallback((smooth: boolean) => {
    if (isMobileBottomNavLayout && !isMobileCreateView) {
      openMobileCreateView(true);
      return;
    }
    focusExistingTopicInput(smooth);
  }, [
    focusExistingTopicInput,
    isMobileBottomNavLayout,
    isMobileCreateView,
    openMobileCreateView,
  ]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    if (!isMobileCreateView) {
      return;
    }
    window.scrollTo({ top: 0, behavior: "auto" });
    if (!pendingMobileComposeFocusRef.current) {
      return;
    }
    pendingMobileComposeFocusRef.current = false;
    window.setTimeout(() => {
      focusExistingTopicInput(false);
    }, 90);
  }, [focusExistingTopicInput, isMobileCreateView, pendingMobileComposeFocusRef]);

  useEffect(() => {
    if (!shouldShowEmptyFeedGuide || typeof window === "undefined") {
      return;
    }
    const onInput = (event: Event) => {
      const target = event.target;
      if (!(target instanceof HTMLTextAreaElement) || target.name !== "topic") {
        return;
      }
      if (!target.value.trim()) {
        return;
      }
      markGuideDismissed();
    };
    document.addEventListener("input", onInput, true);
    return () => document.removeEventListener("input", onInput, true);
  }, [markGuideDismissed, shouldShowEmptyFeedGuide]);

  if (!paginatedGeneratedTextList.isSuccess || !userSettingsQuery.isSuccess) {
    return <LoadingScreen />;
  }

  return (
    <div
      className="min-h-screen px-2 pt-4 pb-[calc(6.5rem+env(safe-area-inset-bottom))] sm:px-3 sm:pt-5 md:pb-4 lg:px-4 xl:px-6 xl:pb-12"
      style={{
        background:
          mobileReeprisePageWash
            ? "transparent"
            : "#f8fafc",
      }}
    >
      <div className="mx-auto w-full max-w-[720px]">
        {userSettingsQuery.data.show_welcome_card && <WelcomeCard />}
        <NotificationsCenter />

        <div className="w-full space-y-8">
          <div
            className="min-w-0 space-y-3 xl:space-y-7"
            data-testid="reeprise-main-column"
          >
            {isMobileCreateView ? (
              <section
                className="space-y-4"
                data-testid="reeprise-mobile-create-view"
              >
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={closeMobileCreateView}
                    className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border bg-white text-slate-600"
                    aria-label={translate("reeprise_mobile_create_back_to_feed")}
                    data-testid="reeprise-mobile-create-close"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
                <div
                  className={`mx-auto w-full ${
                    showComposeGuidePulse && shouldShowEmptyFeedGuide
                      ? "rounded-2xl border border-sky-300"
                      : ""
                  }`}
                  data-testid="reeprise-compose-shell"
                >
                  <GenerateTextCard />
                </div>
              </section>
            ) : (
              <>
                <div
                  className={`mx-auto w-full ${
                    showComposeGuidePulse && shouldShowEmptyFeedGuide
                      ? "rounded-2xl border border-sky-300"
                      : ""
                  }`}
                  data-testid="reeprise-compose-shell"
                >
                  <GenerateTextCard />
                </div>

                <div data-testid="reeprise-utility-row-mobile">
                  <VocabularyPreviewCard />
                </div>

                <section className="space-y-4">
                  <ul
                    className="grid w-full grid-cols-1 gap-3 self-start"
                    data-testid="reeprise-feed-list"
                  >
                    {languageSpace === "ITALIAN" ? (
                      <li data-testid="reeprise-italian-practice-item">
                        <ItalianPracticeCallout />
                      </li>
                    ) : null}
                    <li data-testid="reeprise-daily-feed-item">
                      <DailyPracticeCallout
                        generatedTexts={visibleTexts}
                        languageSpace={languageSpace}
                      />
                    </li>
                    {unreadTexts.map((generatedText) => {
                      return (
                        <ReepriseTextCard
                          key={generatedText.id}
                          generatedText={generatedText}
                        />
                      );
                    })}
                    {unreadTexts.length > 0 && readTexts.length > 0 ? (
                      <li
                        aria-hidden="true"
                        data-testid="reeprise-feed-read-divider"
                        className="my-1 border-t border-slate-200"
                      />
                    ) : null}
                    {readTexts.map((generatedText) => {
                      return (
                        <ReepriseTextCard
                          key={generatedText.id}
                          generatedText={generatedText}
                        />
                      );
                    })}
                    {!hasVisibleFeedItems ? (
                      <li>
                        <FeedEmptyState
                          showFlowHint={
                            shouldShowEmptyFeedGuide && !isMobileBottomNavLayout
                          }
                          onGuideToComposer={() => focusTopicInputInCompose(true)}
                        />
                      </li>
                    ) : null}
                    {hasVisibleFeedItems &&
                      paginatedGeneratedTextList.hasNextPage && (
                        <li className="mb-1 w-full">
                          <button
                            className="w-full rounded-xl border bg-neutral-50 px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:border-slate-300 hover:text-slate-900"
                            onClick={() => {
                              paginatedGeneratedTextList.fetchNextPage();
                            }}
                          >
                            {translate("reeprise_feed_load_more")}
                          </button>
                        </li>
                    )}
                  </ul>
                </section>
              </>
            )}
          </div>
        </div>
      </div>
      {shouldShowEmptyFeedGuide && isMobileBottomNavLayout ? (
        <button
          type="button"
          onClick={() => focusTopicInputInCompose(true)}
          className="fixed right-3 bottom-20 z-30 rounded-full border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700"
          data-testid="reeprise-empty-feed-mobile-hint"
        >
          {translate("reeprise_feed_empty_mobile_hint")}
        </button>
      ) : null}
    </div>
  );
}

function FeedEmptyState({
  showFlowHint,
  onGuideToComposer,
}: {
  showFlowHint: boolean;
  onGuideToComposer: () => void;
}) {
  const { translate } = useTranslation();

  return (
    <div
      className="flex flex-col items-center rounded-3xl border bg-white px-6 py-8 text-center sm:px-8 sm:py-10"
      data-testid="reeprise-feed-empty-state"
    >
      <div className="relative mb-5 flex h-28 w-28 items-center justify-center rounded-full border bg-slate-50">
        <div className="absolute -top-2 left-5 h-4 w-4 rounded-full bg-sky-100" />
        <div className="absolute right-4 bottom-3 h-3 w-3 rounded-full bg-emerald-100" />
        <svg
          aria-hidden="true"
          viewBox="0 0 64 64"
          className="h-14 w-14 text-slate-500"
          fill="none"
        >
          <rect
            x="14"
            y="16"
            width="36"
            height="30"
            rx="6"
            stroke="currentColor"
            strokeWidth="2.5"
          />
          <path
            d="M24 24h16M24 31h10M24 38h12"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
        </svg>
      </div>
      <p className="mb-1 font-serif text-xl font-semibold text-slate-900">
        {translate("reeprise_feed_empty_title")}
      </p>
      <p className="max-w-[34rem] text-sm leading-6 text-slate-600 sm:text-[0.95rem]">
        {translate("reeprise_feed_empty_body")}
      </p>
      <button
        type="button"
        onClick={onGuideToComposer}
        className="mt-4 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700"
      >
        {translate("reeprise_feed_empty_guide_hint")}
      </button>
      {showFlowHint ? (
        <div className="mt-3 flex items-center gap-2 text-[11px] text-slate-500">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-sky-300" />
          <span>{translate("reeprise_feed_empty_flow_hint")}</span>
        </div>
      ) : null}
    </div>
  );
}


function DailyPracticeCallout({
  generatedTexts,
  languageSpace,
  layout = "default",
}: {
  generatedTexts: GeneratedText[];
  languageSpace: string;
  layout?: "default" | "desktopStrip";
}) {
  const isDesktopStrip = layout === "desktopStrip";
  const { translate, language: uiLanguage } = useTranslation();
  const navigate = useNavigate();
  const mutation = useDailyTextCreate();
  const resetDailyTextCreate = mutation.reset;
  const queryClient = useQueryClient();
  const userSettings = useUserSettings({ enabled: true });
  const [createdTodayByLanguage, setCreatedTodayByLanguage] = useLocalStorage<
    Record<string, DailyDoneMarker>
  >(DAILY_TEXT_DONE_MARKER_KEY, {});
  const autoCreateRequestKeyRef = useRef<string | null>(null);
  const resetDailyMutationKeyRef = useRef<string | null>(null);
  const draftSubtitleRequestKeyRef = useRef<string | null>(null);
  const [draftTitleInMotherTongue, setDraftTitleInMotherTongue] = useState("");

  const now = new Date();
  const isMorningOrLater = now.getHours() >= 6;
  const todayIso = getLocalIsoDate(now);
  const locale = uiLanguage === "GERMAN" ? "de-DE" : "en-US";
  const weekdayLabel = new Intl.DateTimeFormat(locale, {
    weekday: "long",
  }).format(now);
  const dateLabel = new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "short",
  }).format(now);

  // Reset mutation state when switching language or day to avoid cross-language success messages.
  useEffect(() => {
    const resetKey = `${languageSpace}:${todayIso}`;
    if (resetDailyMutationKeyRef.current === null) {
      resetDailyMutationKeyRef.current = resetKey;
      return;
    }
    if (resetDailyMutationKeyRef.current === resetKey) {
      return;
    }
    resetDailyMutationKeyRef.current = resetKey;
    resetDailyTextCreate();
  }, [languageSpace, resetDailyTextCreate, todayIso]);

  const resolvedLanguage = languageSpace === "ALL" ? "ENGLISH" : languageSpace;
  const markerForLanguageSpace = createdTodayByLanguage[languageSpace];
  const markerForResolvedLanguage = createdTodayByLanguage[resolvedLanguage];
  const markerDateForLanguageSpace = getDoneMarkerDate(markerForLanguageSpace);
  const markerDateForResolvedLanguage = getDoneMarkerDate(
    markerForResolvedLanguage,
  );

  // If we stored a local creation marker for this language+day, keep collapsed state stable
  // even if list refetch lags or the route remounts.
  const locallyCreatedToday =
    markerDateForLanguageSpace === todayIso ||
    markerDateForResolvedLanguage === todayIso;

  const todayPracticeText = findMostRecentDailyNudgeTextForDate(
    generatedTexts,
    todayIso,
  );
  const mutationDailyText =
    mutation.data &&
    getLocalIsoDateFromTimestamp(mutation.data.created_at) === todayIso &&
    mutation.data.origin === "DAILY_NUDGE" &&
    mutation.data.language === resolvedLanguage
      ? mutation.data
      : null;
  const displayReadyTodayPracticeText = preferDisplayReadyDailyText(
    todayPracticeText,
    mutationDailyText,
  );
  const fallbackTodayPracticeText = displayReadyTodayPracticeText ?? todayPracticeText ?? mutationDailyText;
  const hasTodayPractice = Boolean(
    todayPracticeText || fallbackTodayPracticeText,
  );
  const [doneLatched, setDoneLatched] = useState(
    hasTodayPractice || locallyCreatedToday,
  );

  useEffect(() => {
    // Reset latch for a new day/language bucket.
    setDoneLatched(hasTodayPractice || locallyCreatedToday);
  }, [hasTodayPractice, languageSpace, locallyCreatedToday, todayIso]);

  useEffect(() => {
    // Once done is visible, keep it stable through transient query/cache updates.
    if (!doneLatched && (hasTodayPractice || locallyCreatedToday)) {
      setDoneLatched(true);
    }
  }, [doneLatched, hasTodayPractice, locallyCreatedToday]);

  useEffect(() => {
    if (!isMorningOrLater || hasTodayPractice || mutation.isPending) {
      return;
    }
    const requestKey = `${todayIso}:${resolvedLanguage}`;
    if (autoCreateRequestKeyRef.current === requestKey) {
      return;
    }
    autoCreateRequestKeyRef.current = requestKey;
    void mutation
      .mutateAsync({ language: resolvedLanguage })
      .then((created) => {
        const nextMarker = {
          date: todayIso,
          textId: created.id,
          title: resolvePendingDailyTitle(created) ?? undefined,
          motherTongueTitle: undefined,
        };
        setCreatedTodayByLanguage((prev) => {
          const withLanguageSpace = upsertDoneMarker(
            prev,
            languageSpace,
            nextMarker,
          );
          return upsertDoneMarker(
            withLanguageSpace,
            resolvedLanguage,
            nextMarker,
          );
        });
      })
      .catch(() => {
        // surfaced through mutation state
      });
  }, [
    hasTodayPractice,
    isMorningOrLater,
    languageSpace,
    mutation,
    resolvedLanguage,
    setCreatedTodayByLanguage,
    todayIso,
  ]);

  const handleCreate = async () => {
    try {
      const created = await mutation.mutateAsync({
        language: resolvedLanguage,
      });
      setCreatedTodayByLanguage((prev) => {
        const nextMarker = {
          date: todayIso,
          textId: created?.id,
          title: resolvePendingDailyTitle(created) ?? undefined,
          motherTongueTitle: undefined,
        };
        const withLanguageSpace = upsertDoneMarker(
          prev,
          languageSpace,
          nextMarker,
        );
        return upsertDoneMarker(
          withLanguageSpace,
          resolvedLanguage,
          nextMarker,
        );
      });
      void queryClient.invalidateQueries({
        queryKey: [REEPRISE_LIST_QUERY_KEY],
      });
      if (created?.id) {
        navigate({
          to: "/app/reeprise/$generatedTextId",
          params: { generatedTextId: `${created.id}` },
          search: {
            chapter: undefined,
            focusWord: undefined,
            focusSentence: undefined,
            focusToken: undefined,
            jumpTo: undefined,
          },
        });
      }
    } catch {
      // handled by mutation error state
    }
  };

  const disabled = mutation.isPending;
  const markerTextIdForToday =
    markerDateForLanguageSpace === todayIso
      ? getDoneMarkerTextId(markerForLanguageSpace)
      : markerDateForResolvedLanguage === todayIso
        ? getDoneMarkerTextId(markerForResolvedLanguage)
        : undefined;
  const markerTitleForToday =
    markerDateForLanguageSpace === todayIso
      ? getDoneMarkerTitle(markerForLanguageSpace)
      : markerDateForResolvedLanguage === todayIso
        ? getDoneMarkerTitle(markerForResolvedLanguage)
        : undefined;
  const markerMotherTongueTitleForToday =
    markerDateForLanguageSpace === todayIso
      ? getDoneMarkerMotherTongueTitle(markerForLanguageSpace)
      : markerDateForResolvedLanguage === todayIso
        ? getDoneMarkerMotherTongueTitle(markerForResolvedLanguage)
        : undefined;
  const openTextId =
    todayPracticeText?.id ??
    displayReadyTodayPracticeText?.id ??
    fallbackTodayPracticeText?.id ??
    markerTextIdForToday;
  const listResolvedDoneCardText = openTextId
    ? generatedTexts.find((text) => text.id === openTextId)
    : undefined;
  const doneCardText = preferDisplayReadyDailyText(
    listResolvedDoneCardText,
    fallbackTodayPracticeText,
  );
  const doneCardTitleSource = preferDisplayReadyDailyText(
    doneCardText,
    displayReadyTodayPracticeText,
  );
  const pendingMutationDailyTitle = resolvePendingDailyTitle(mutationDailyText);
  const resolvedOpenTextId = doneCardText?.id;
  const canOpenDoneCard = Boolean(resolvedOpenTextId);
  const isDraftOnlyDailyText =
    doneCardText?.origin === "DAILY_NUDGE" && doneCardText.status === "PENDING";
  const doneCardTitle = useMemo(() => {
    if (pendingMutationDailyTitle) {
      return compactDailyDoneTitle(pendingMutationDailyTitle);
    }
    if (!doneCardTitleSource) {
      return compactDailyDoneTitle(markerTitleForToday ?? null);
    }
    return compactDailyDoneTitle(
      resolveDailyCardTitle(doneCardTitleSource) ?? markerTitleForToday ?? null,
    );
  }, [doneCardTitleSource, markerTitleForToday, pendingMutationDailyTitle]);
  const motherTongue = userSettings.data?.mother_tongue ?? "GERMAN";
  const doneCardDescription = useMemo(() => {
    const translatedTitle =
      draftTitleInMotherTongue.trim() ||
      markerMotherTongueTitleForToday?.trim() ||
      "";
    if (translatedTitle) {
      return translatedTitle;
    }
    return translate("dailyTextDoneDescription");
  }, [draftTitleInMotherTongue, markerMotherTongueTitleForToday, translate]);
  const activeThemeClasses = getDailyCardThemeClasses(languageSpace);
  const doneThemeClasses = getDailyCardThemeClasses(languageSpace);

  useEffect(() => {
    if (
      !doneCardText ||
      doneCardText.origin !== "DAILY_NUDGE"
    ) {
      setDraftTitleInMotherTongue("");
      return;
    }

    const title = (doneCardTitle ?? "").trim();
    if (!title) {
      setDraftTitleInMotherTongue("");
      return;
    }
    if (motherTongue === doneCardText.language) {
      setDraftTitleInMotherTongue(title);
      return;
    }

    const requestKey = `${doneCardText.id}:${title}:${motherTongue}:${doneCardText.language}`;
    if (draftSubtitleRequestKeyRef.current === requestKey) {
      return;
    }
    draftSubtitleRequestKeyRef.current = requestKey;
    setDraftTitleInMotherTongue("");

    const abortController = new AbortController();
    void fetch("/api/reeprise/translate/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFTOKEN": getCsrfCookie(),
      },
      credentials: "include",
      signal: abortController.signal,
      body: JSON.stringify({
        text: title,
        learning_lang: motherTongue,
        source_lang: doneCardText.language,
        save: false,
      }),
    })
      .then(async (response) => {
        if (!response.ok) {
          return;
        }
        const data = (await response.json()) as { translation?: string };
        const translatedTitle = (data.translation || "").trim();
        if (translatedTitle) {
          setDraftTitleInMotherTongue(translatedTitle);
        }
      })
      .catch(() => {
        // Keep the card usable even if subtitle translation fails.
      });

    return () => {
      abortController.abort();
    };
  }, [doneCardText, doneCardTitle, motherTongue]);
  useEffect(() => {
    const translatedTitle = draftTitleInMotherTongue.trim();
    if (!translatedTitle) {
      return;
    }
    if (markerMotherTongueTitleForToday?.trim() === translatedTitle) {
      return;
    }
    setCreatedTodayByLanguage((prev) => {
      const nextMarker = {
        date: todayIso,
        textId: resolvedOpenTextId ?? markerTextIdForToday,
        title: doneCardTitle ?? markerTitleForToday ?? undefined,
        motherTongueTitle: translatedTitle,
      };
      const withLanguageSpace = upsertDoneMarker(
        prev,
        languageSpace,
        nextMarker,
      );
      return upsertDoneMarker(withLanguageSpace, resolvedLanguage, nextMarker);
    });
  }, [
    doneCardTitle,
    draftTitleInMotherTongue,
    languageSpace,
    markerMotherTongueTitleForToday,
    markerTextIdForToday,
    markerTitleForToday,
    resolvedLanguage,
    resolvedOpenTextId,
    setCreatedTodayByLanguage,
    todayIso,
  ]);
  useEffect(() => {
    if (!resolvedOpenTextId) {
      return;
    }
    const markerHasTextIdForToday =
      (markerDateForLanguageSpace === todayIso &&
        getDoneMarkerTextId(markerForLanguageSpace) === resolvedOpenTextId) ||
      (markerDateForResolvedLanguage === todayIso &&
        getDoneMarkerTextId(markerForResolvedLanguage) === resolvedOpenTextId);
    if (markerHasTextIdForToday) {
      return;
    }
    setCreatedTodayByLanguage((prev) => {
      const nextMarker = {
        date: todayIso,
        textId: resolvedOpenTextId,
        title: doneCardTitle ?? markerTitleForToday ?? undefined,
        motherTongueTitle:
          draftTitleInMotherTongue.trim() ||
          markerMotherTongueTitleForToday?.trim() ||
          undefined,
      };
      const withLanguageSpace = upsertDoneMarker(
        prev,
        languageSpace,
        nextMarker,
      );
      return upsertDoneMarker(withLanguageSpace, resolvedLanguage, nextMarker);
    });
  }, [
    resolvedOpenTextId,
    markerDateForLanguageSpace,
    markerDateForResolvedLanguage,
    markerForLanguageSpace,
    markerForResolvedLanguage,
    todayIso,
    languageSpace,
    doneCardTitle,
    draftTitleInMotherTongue,
    markerMotherTongueTitleForToday,
    markerTitleForToday,
    resolvedLanguage,
    setCreatedTodayByLanguage,
  ]);
  const doneContainerClass = `${doneThemeClasses.container.replace("mb-4", "mb-0")} ${isDesktopStrip ? "h-full !p-3" : ""} ${canOpenDoneCard ? "cursor-pointer hover:cursor-pointer" : ""}`;
  const createContainerClass = `${activeThemeClasses.container.replace("mb-4", "mb-0")} ${isDesktopStrip ? "h-full !p-3" : ""} ${disabled ? "cursor-not-allowed" : "cursor-pointer hover:cursor-pointer"}`;
  if (!isMorningOrLater) {
    return null;
  }

  if (doneLatched) {
    return (
      <>
        <div
          data-testid="reeprise-daily-primary-card"
          className={doneContainerClass}
          role={canOpenDoneCard ? "button" : undefined}
          tabIndex={canOpenDoneCard ? 0 : undefined}
          onClick={
            canOpenDoneCard
              ? () =>
                  navigate({
                    to: "/app/reeprise/$generatedTextId",
                    params: { generatedTextId: `${resolvedOpenTextId}` },
                    search: {
                      chapter: undefined,
                      focusWord: undefined,
                      focusSentence: undefined,
                      focusToken: undefined,
                      jumpTo: undefined,
                    },
                  })
              : undefined
          }
          onKeyDown={
            canOpenDoneCard
              ? (event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    navigate({
                      to: "/app/reeprise/$generatedTextId",
                      params: { generatedTextId: `${resolvedOpenTextId}` },
                      search: {
                        chapter: undefined,
                        focusWord: undefined,
                        focusSentence: undefined,
                        focusToken: undefined,
                        jumpTo: undefined,
                      },
                    });
                  }
                }
              : undefined
          }
        >
          <div className="relative">
            <div className="mb-3 flex items-center justify-between gap-2">
              <p className={doneThemeClasses.badgeText}>{weekdayLabel}</p>
              <p className={doneThemeClasses.dateText}>{dateLabel}</p>
            </div>
            <div
              className={
                isDesktopStrip
                  ? "grid gap-2"
                  : "grid gap-3"
              }
            >
              <div className="min-w-0">
                <p className={doneThemeClasses.headingText}>
                  {doneCardTitle ?? translate("dailyTextDoneHeading")}
                </p>
                <p
                  className={`${doneThemeClasses.bodyText} ${isDesktopStrip ? "hidden" : ""}`}
                >
                  {doneCardDescription}
                </p>
              </div>
              {canOpenDoneCard ? (
                <p
                  className={`${doneThemeClasses.actionText} ${isDesktopStrip ? "mt-1 text-[10px]" : ""}`}
                >
                  {translate(
                    isDraftOnlyDailyText
                      ? "dailyTextDraftOpen"
                      : "dailyTextDoneOpen",
                  )}
                  <ArrowRightIcon className="h-4 w-4" />
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div
        data-testid="reeprise-daily-primary-card"
        className={createContainerClass}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={disabled ? undefined : () => void handleCreate()}
        onKeyDown={
          disabled
            ? undefined
            : (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  void handleCreate();
                }
              }
        }
      >
        <div className="relative">
          <div className="mb-3 flex items-center justify-between gap-2">
            <p className={activeThemeClasses.badgeText}>{weekdayLabel}</p>
            <p className={activeThemeClasses.dateText}>{dateLabel}</p>
          </div>
          <div
            className={
              isDesktopStrip
                ? "grid gap-2"
                : "grid gap-3"
            }
          >
            <div className="min-w-0">
              <p
                className={`${activeThemeClasses.headingText} ${isDesktopStrip ? "text-base sm:text-base" : ""}`}
              >
                {translate("dailyTextHeading")}
              </p>
              <p
                className={`${activeThemeClasses.bodyText} ${isDesktopStrip ? "hidden" : ""}`}
              >
                {translate("dailyTextDescription")}
              </p>
            </div>
            <button
              onClick={(event) => {
                event.stopPropagation();
                void handleCreate();
              }}
              disabled={disabled}
              className={`${activeThemeClasses.actionText} ${isDesktopStrip ? "mt-1 text-[10px]" : ""}`}
            >
              {mutation.isPending
                ? translate("dailyTextLoading")
                : translate("dailyTextCta")}
              {!mutation.isPending ? (
                <ArrowRightIcon className="h-4 w-4" />
              ) : null}
            </button>
          </div>
        </div>
        {mutation.isError && (
          <p className="relative mt-4 text-xs text-rose-600">
            {mutation.error?.message ?? translate("dailyTextError")}
          </p>
        )}
        {mutation.isSuccess && (
          <p className="relative mt-4 text-xs font-medium text-emerald-800">
            {translate("dailyTextSuccess")}
          </p>
        )}
      </div>
    </>
  );
}

function WelcomeCard() {
  const loginStatus = useLoginStatus();

  const updateUserSettings = useUserSettingsUpdate();

  if (!loginStatus.isSuccess || loginStatus.data.state === "logged out") {
    return <></>;
  }

  return (
    <div className="max-w-2xl bg-white shadow-xl rounded-2xl relative p-8 mb-10 text-center border-2 border-blue-200 hover:border-blue-300">
      <div
        className="block absolute right-4 top-5 cursor-pointer"
        onClick={() => {
          updateUserSettings.mutate({
            userSettingsDto: {
              show_welcome_card: false,
            },
          });
        }}
      >
        <XMarkIcon className="w-8 h-8 mr-1" />
      </div>
      <h1 className="text-3xl font-bold text-[#1e293b] mt-5">
        Hi {loginStatus.data.username}!
      </h1>

      <p className="mt-4 text-[#1e293b]">
        Erstelle in wenigen Schritten deine eigenen Sprachlern-Texte:
      </p>

      <div className="mt-6 text-left space-y-4 text-[#1e293b]">
        <div className="flex items-start space-x-3">
          <span className="text-xl text-[#86efac]">1️⃣</span>
          <p>
            <span className="font-semibold text-[#1e293b]">
              Thema festlegen
            </span>{" "}
            – Wähle, worüber dein Text handeln soll.
          </p>
        </div>

        <div className="flex items-start space-x-3">
          <span className="text-xl text-[#86efac]">2️⃣</span>
          <div>
            <p className="font-semibold text-[#1e293b]">
              Details anpassen – Unter „Weitere Optionen“ kannst du:
            </p>
            <ul className="list-disc list-inside mt-2 text-[#1e293b]">
              <li>Lern-Sprache und Sprach-Level (A0–C2) wählen</li>
              <li>
                Eine Zeitform für deinen Text festlegen (Präsens,Perfekt,...)
              </li>
              <li>Den Text-Typ festlegen (z. B. Geschichte, Dialog)</li>
              <li>Die gewünschte Textlänge anhand der Wortanzahl definieren</li>
            </ul>
          </div>
        </div>

        <div className="flex items-start space-x-3">
          <span className="text-xl text-[#86efac]">3️⃣</span>
          <p>
            <span className="font-semibold text-[#1e293b]">
              Lass dich inspirieren!
            </span>{" "}
            – Lade ein Bild hoch, und dein Text wird von dessen Inhalt und
            Stimmung beeinflusst.
          </p>
        </div>
      </div>

      <div
        className="w-full mt-6 bg-blue-500 text-white p-3 rounded-lg hover:bg-blue-600 transition duration-200 cursor-pointer"
        onClick={() => {
          updateUserSettings.mutate({
            userSettingsDto: {
              show_welcome_card: false,
            },
          });
        }}
      >
        <p className="font-bold">Jetzt Text erstellen</p>
      </div>
    </div>
  );
}

function NotificationsCenter() {
  const { translate } = useTranslation();
  const notificationsQuery = useNotifications();
  const markRead = useMarkNotificationAsRead();

  if (!notificationsQuery.isSuccess) {
    return null;
  }
  const unreadShares = notificationsQuery.data.filter(
    (notification) =>
      !notification.is_read && notification.notification_type === "TEXT_SHARED",
  );
  if (unreadShares.length === 0) {
    return null;
  }
  return (
    <div className="mb-4 space-y-3">
      {unreadShares.map((notification) => {
        const payload = notification.payload as {
          text_id?: number;
          topic?: string;
          sender_username?: string;
        };
        return (
          <div
            key={notification.id}
            className="rounded-2xl border border-blue-200 bg-blue-50 p-4 shadow-sm"
          >
            <p className="text-sm text-slate-800">
              {translate("notifications_share_message", {
                username: payload.sender_username ?? "Jemand",
                topic:
                  payload.topic ??
                  translate("notifications_share_default_topic"),
              })}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {payload.text_id && (
                <Link
                  to="/app/reeprise/$generatedTextId"
                  params={{ generatedTextId: `${payload.text_id}` }}
                  search={{
                    chapter: undefined,
                    focusWord: undefined,
                    focusSentence: undefined,
                    focusToken: undefined,
                    jumpTo: undefined,
                  }}
                  className="rounded-full bg-blue-600 px-3 py-1 text-xs font-semibold text-white hover:bg-blue-700"
                >
                  {translate("notifications_share_open")}
                </Link>
              )}
              <button
                type="button"
                onClick={() => markRead.mutate(notification.id)}
                className="rounded-full border border-blue-300 px-3 py-1 text-xs font-semibold text-blue-700 hover:bg-white disabled:opacity-50"
                disabled={markRead.isPending}
              >
                {translate("notifications_share_dismiss")}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
